import { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { Box, Flex } from '../ui/Layout'
import { loadVideoLite, formatSeconds } from './helpers'
import { useModal } from '../Modal.js'
import Icon from '../ui/Icon.js'
import { PopularButton } from './ui/PopularButton'
import { getAspectRatio } from './EmbedCarousel'
import { ImageExpandButton } from './ui/ImageExpandButton'

// SPECIFIC STUFF FOR THUMBNAILS
const ThumbnailHover = styled(Flex)`
  position: relative;
  &&:hover {
    .thumbnail-button {
      display: block;
    }
  }
`

const ThumbnailContainer = styled(Flex)`
  .hover-text {
    display: none;
  }
  &&:hover {
    .hover-text {
      display: block;
    }
  }
`

const SlideItem = styled(Box)`
  @keyframes loading-gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: -100% 50%;
    }
    100% {
      background-position: -200% 50%;
    }
  }
  cursor: pointer;
  border-radius: ${(props) => `${props.borderRadius}px`};
  box-sizing: border-box;
  @media (min-width: 700px) {
    &&:hover {
      .hover {
        display: flex;
      }
    }
  }
`

const VideoImage = styled.img`
  box-sizing: border-box;
  border-radius: ${(props) => `${props.borderRadius}px`};
  object-fit: cover;
  width: 100% !important;
  height: 100% !important;
  margin-top: 0px !important;
  margin-left: 0px !important;
  margin-right: 0px !important;
  margin-bottom: 0px !important;
  ${(props) =>
    props.widgetThumbnailBorder
      ? `border: ${props.widgetThumbnailBorderSize || 3}px solid ${props.widgetThumbnailBorderColor || 'black'};`
      : ''}
  background: linear-gradient(to right, #e9e9e9, #f9f9f9, #e9e9e9);
  background: -webkit-linear-gradient(to right, #e9e9e9, #f9f9f9, #e9e9e9);
  animation: loading-gradient 4s linear infinite;
  background-size: 200%;
`

const VideoContainer = styled(Flex)`
  transition: opacity 0.5s;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
`

const Video = styled.video`
  box-sizing: border-box;
  border-radius: ${(props) => `${props.borderRadius}px`};
  width: ${(props) => props.videoWidth}px !important;
  height: ${(props) => props.videoHeight}px !important;
  object-fit: cover;
  z-index: 1;
  ${(props) =>
    props.isCircle && props.widgetThumbnailBorder && `border: 2.5px solid white; box-sizing: border-box; border-radius: 100%;`}
  &&::-webkit-media-controls {
    display: none !important;
  }
  /* Could Use thise as well for Individual Controls */
  &&::-webkit-media-controls-play-button {
    display: none !important;
  }
  &&::-webkit-media-controls-volume-slider {
    display: none !important;
  }
  &&::-webkit-media-controls-mute-button {
    display: none !important;
  }
  &&::-webkit-media-controls-timeline {
    display: none !important;
  }
  &&::-webkit-media-controls-current-time-display {
    display: none !important;
  }
  ${(props) =>
    props.widgetThumbnailBorder
      ? `border: ${props.widgetThumbnailBorderSize || 3}px solid ${props.widgetThumbnailBorderColor || 'black'};`
      : ''}
`

const VideoTitle = styled.p`
  font-family: ${(props) => (props.widgetTitleFontFamily ? props.widgetTitleFontFamily.replace(';') : 'inherit')} !important;
  font-weight: ${(props) => props.widgetTitleFontWeight || '700'} !important;
  font-size: ${(props) => props.widgetDesktopTitleFontSize || '18'}px !important;
  line-height: ${(props) => props.widgetDesktopTitleFontSize || '18'}px !important;
  text-align: ${(props) => (props.widgetTitleAlignment ? props.widgetTitleAlignment.toLowerCase() : 'center')} !important;
  color: ${(props) => props.widgetTitleFontColor || 'black'};
  text-decoration: none !important;
  margin-bottom: 16px !important;
  margin-top: 0px !important;
  padding: 0px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  word-break: break-word;
  a,
  a:visited,
  a:active,
  a:hover,
  a:focus {
    color: ${(props) => props.widgetTitleFontColor || 'black'};
  }
`

const VideoSubtitle = styled.p`
  font-family: ${(props) =>
    props.widgetSubtitleFontFamily ? props.widgetSubtitleFontFamily.replace(';') : 'inherit'} !important;
  font-weight: ${(props) => props.widgetSubtitleFontWeight || '500'} !important;
  font-size: ${(props) => props.widgetDesktopSubtitleFontSize || '18'}px !important;
  line-height: ${(props) => props.widgetDesktopSubtitleFontSize || '18'}px !important;
  text-align: ${(props) => (props.widgetTitleAlignment ? props.widgetTitleAlignment.toLowerCase() : 'center')} !important;
  color: ${(props) => props.widgetSubtitleFontColor || 'black'};
  text-decoration: none !important;
  margin-bottom: 16px !important;
  margin-top: 0px !important;
  padding: 0px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  word-break: break-word;
  a,
  a:visited,
  a:active,
  a:hover,
  a:focus {
    color: ${(props) => props.widgetSubtitleFontColor || 'black'};
  }
`

const TitleContainer = styled(Flex)`
  flex-direction: column;
`

const Titles = ({
  isStories,
  videoWidth,
  gapWidth,
  titleVideoObject,
  widgetTitleAlignment,
  widgetDesktopTitleFontSize,
  widgetTitleFontColor,
  widgetTitleFontFamily,
  widgetTitleFontWeight,
  widgetSubtitleFontWeight,
  widgetSubtitleFontFamily,
  widgetSubtitleFontColor,
  widgetDesktopSubtitleFontSize,
  hideMarginLeft,
  hideMarginRight,
  isMobile,
  widgetMobileTitleFontSize,
  widgetMobileSubtitleFontSize
}) => {
  return (
    <TitleContainer
      maxWidth={videoWidth}
      mr={hideMarginRight ? 0 : `${gapWidth / 2}px`}
      ml={hideMarginLeft ? 0 : `${gapWidth / 2}px`}
      width="auto"
    >
      {titleVideoObject.title && (
        <VideoTitle
          widgetTitleAlignment={widgetTitleAlignment}
          widgetDesktopTitleFontSize={isMobile ? widgetMobileTitleFontSize : widgetDesktopTitleFontSize}
          widgetTitleFontColor={widgetTitleFontColor}
          widgetTitleFontFamily={widgetTitleFontFamily}
          widgetTitleFontWeight={widgetTitleFontWeight}
          dangerouslySetInnerHTML={{ __html: titleVideoObject.title }}
        ></VideoTitle>
      )}
      {titleVideoObject.subtitle && !isStories && (
        <VideoSubtitle
          widgetTitleAlignment={widgetTitleAlignment}
          widgetDesktopSubtitleFontSize={isMobile ? widgetMobileSubtitleFontSize : widgetDesktopSubtitleFontSize}
          widgetSubtitleFontColor={widgetSubtitleFontColor}
          widgetSubtitleFontFamily={widgetSubtitleFontFamily}
          widgetSubtitleFontWeight={widgetSubtitleFontWeight}
          dangerouslySetInnerHTML={{ __html: titleVideoObject.subtitle }}
        ></VideoSubtitle>
      )}
    </TitleContainer>
  )
}

const InnerTitleContainer = styled(Flex)`
  box-sizing: border-box !important;
  z-index: 1;
  position: absolute;
  left: 0px;
  padding-left: 16px;
  padding-right: 16px;
  height: 150px;

  width: 100%;
  ${(props) =>
    props.widgetTitlePosition === 'INSIDE' &&
    `border-radius: 0 0 ${props.borderRadius}px ${props.borderRadius}px;background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%);
    bottom: 0px;flex-direction: column-reverse;`}
  ${(props) =>
    props.widgetTitlePosition === 'BOTTOM' &&
    `border-radius: 0 0 ${props.borderRadius}px ${props.borderRadius}px;background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%);
    bottom: 0px;flex-direction: column-reverse;`}
  ${(props) =>
    props.widgetTitlePosition === 'TOP' &&
    `border-radius: ${props.borderRadius}px ${props.borderRadius}px 0 0;background: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%);
    top:0px;flex-direction: column;padding-top: 16px;`}
   ${(props) =>
    props.widgetTitlePosition === 'MIDDLE' &&
    `border-radius:${props.borderRadius}px;background-color: rgba(10,18,0,0.2);top:0px;flex-direction: column;padding-top: 16px;height: 100%;justify-content:center;h`}
`

const StyledPicture = styled.picture`
  display: block;
  height: 100% !important;
`

const StyledNextPicture = styled.picture`
  z-index: -1;
  height: 100% !important;
  position: absolute;
  left: 0;
  top: 0;
`

const PlayButton = styled.svg`
  fill: none !important;
  width: ${(props) => props.width}px !important;
  height: ${(props) => props.height}px !important;
  position: absolute;
  z-index: 2;

  @keyframes cliparapulse {
    0% {
      box-shadow: 0 0 0 0 ${(props) => props.playButtonColor};
    }
    100% {
      box-shadow: 0 0 0 8px #fbb32f01;
    }
  }
  border-radius: 60px;
  cursor: pointer;
  ${(props) => props.animate && `animation: cliparapulse 1500ms infinite;`}
  @media(max-width: 1000px) {
    width: 40px !important;
    height: 40px !important;
  }
  ${(props) => {
    if (props.widgetPlayButtonPosition === 'BOTTOM_LEFT') {
      return `bottom: 16px; left: 16px;`
    }
    if (props.widgetPlayButtonPosition === 'BOTTOM_RIGHT') {
      return `bottom: 16px; right: 16px;`
    }
    return ` top: 50%; left: 50%; transform: translate(-50%, -50%);`
  }}
`

const PlayButtonComponent = ({ campaign, pbWidth }) => {
  if (campaign.type !== 'GRID') {
    return null
  }
  return (
    <PlayButton
      widgetPlayButtonPosition={campaign.widgetPlayButtonPosition}
      animate={campaign.playButtonAnimation}
      playButtonColor={campaign.playButtonColor || '#333333'}
      width={pbWidth}
      height={pbWidth}
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {campaign.widgetPlayButtonStyle === 'FILLED' ? (
        <>
          <circle cx="30" cy="30" r="30" fill={campaign.playButtonColor || '#333333'} />
          <path
            d="M45 28.268C46.3333 29.0378 46.3333 30.9623 45 31.7321L24 43.8564C22.6667 44.6262 21 43.664 21 42.1244L21 17.8756C21 16.336 22.6667 15.3738 24 16.1436L45 28.268Z"
            fill="white"
          />
        </>
      ) : (
        <>
          <circle cx="30" cy="30" r="28.5" stroke={campaign.playButtonColor || '#333333'} stroke-width="3" />
          <path
            d="M46.1097 29.2683C47.4431 30.0381 47.4431 31.9626 46.1097 32.7324L24.9451 44.9518C23.6118 45.7216 21.9451 44.7594 21.9451 43.2198L21.9451 18.781C21.9451 17.2414 23.6118 16.2791 24.9451 17.0489L46.1097 29.2683Z"
            fill={campaign.playButtonColor || '#333333'}
          />
        </>
      )}
    </PlayButton>
  )
}

const NavigationSlideItem = ({
  campaign,
  videoHeight,
  videoWidth,
  activeVideoIndex,
  videosToShow,
  i,
  video,
  videoObjects,
  borderRadius,
  isStories,
  isCircle,
  isMobile,
  noNextImage,
  isPopular
}) => {
  const {
    widgetTitleActive,
    widgetTitlePosition,
    widgetGapWidth,
    widgetThumbnailBorderSize,
    widgetThumbnailBorder,
    widgetThumbnailBorderColor,
    widgetAutoplay,
    videos
  } = campaign
  const [videoPlaying, setVideoPlaying] = useState(false)
  const isVideo = video.type === 'VIDEO'
  const showNextImage = i === Math.ceil(videosToShow + activeVideoIndex - 1) && !noNextImage
  const imageBoostFactor = 1.5
  const isBunnyCdn = video.customThumbnail && video.customThumbnail.includes('clipara.b-cdn.net')
  const imageUrl = video.customThumbnail
    ? isBunnyCdn
      ? `${video.customThumbnail}?width=${Math.round(videoWidth * imageBoostFactor)}&height=${Math.round(
          videoHeight * imageBoostFactor
        )}`
      : video.customThumbnail +
        `-/scale_crop/${Math.round(videoWidth * imageBoostFactor)}x${Math.round(videoHeight * imageBoostFactor)}/center/`
    : `https://image.mux.com/${video.playbackId}/thumbnail.png?time=${video.thumbnailTime || 0.0}&width=${Math.round(
        videoWidth * imageBoostFactor
      )}&height=${Math.round(videoHeight * imageBoostFactor)}&fit_mode=crop`
  const webpUrl = video.customThumbnail
    ? isBunnyCdn
      ? imageUrl
      : video.customThumbnail +
        `-/scale_crop/${Math.round(videoWidth * imageBoostFactor)}x${Math.round(
          videoHeight * imageBoostFactor
        )}/center/-/format/webp/`
    : imageUrl.replace('.png', '.webp')

  // get next image url for lazy load
  let nextImageUrl, nextWebpUrl, nextImageIsVideo, nextImageImageUrl
  if (videos[i + 1]) {
    nextImageIsVideo = videos[i + 1].type === 'VIDEO'
    if (!nextImageIsVideo) {
      nextImageImageUrl = `https://clipara.b-cdn.net/${videos[i + 1].path}?aspect_ratio=${getAspectRatio(
        campaign.widgetThumbnailDimensions
      )}&width=${Math.round(videoWidth * 2)}`
    }
    const nextIsBunnyCdn = videos[i + 1].customThumbnail && videos[i + 1].customThumbnail.includes('clipara.b-cdn.net')
    nextImageUrl = videos[i + 1].customThumbnail
      ? nextIsBunnyCdn
        ? `${videos[i + 1].customThumbnail}?width=${Math.round(videoWidth * imageBoostFactor)}&height=${Math.round(
            videoHeight * imageBoostFactor
          )}`
        : videos[i + 1].customThumbnail +
          `-/scale_crop/${Math.round(videoWidth * imageBoostFactor)}x${Math.round(videoHeight * imageBoostFactor)}/center/`
      : `https://image.mux.com/${videos[i + 1].playbackId}/thumbnail.png?time=${
          videos[i + 1].thumbnailTime || 0.0
        }&width=${Math.round(videoWidth * imageBoostFactor)}&height=${Math.round(videoHeight * imageBoostFactor)}&fit_mode=crop`
    nextWebpUrl = videos[i + 1].customThumbnail
      ? nextIsBunnyCdn
        ? nextImageUrl
        : videos[i + 1].customThumbnail +
          `-/scale_crop/${Math.round(videoWidth * imageBoostFactor)}x${Math.round(
            videoHeight * imageBoostFactor
          )}/center/-/format/webp/`
      : nextImageUrl.replace('.png', '.webp')
  }

  const titleVideoObject = videoObjects.find((vo) => vo.videoId.toString() === video.id.toString() && vo.type === 'TITLE') || {}
  const buttonVideoObject = videoObjects.find((vo) => vo.videoId.toString() === video.id.toString() && vo.type === 'BUTTON') || {}

  // Specific Stuff for thumbnails
  const { setAddThumbnailVideo, setShowAddThumbnailModal } = useModal()
  return (
    <SlideItem
      key={video.id}
      className="keen-slider__slide"
      borderRadius={borderRadius}
      widgetThumbnailBorderSize={widgetThumbnailBorderSize}
      widgetThumbnailBorderColor={widgetThumbnailBorderColor}
      widgetThumbnailBorder={widgetThumbnailBorder}
      onMouseEnter={async () => {
        if (widgetAutoplay && !isStories && video.type === 'VIDEO') {
          videos.forEach(async (elVideo, i) => {
            if (video.id === elVideo.id) {
              return
            }
            const element = document.getElementById(`__clipara-navigation-carousel-${elVideo.id}-${campaign.id}`)
            if (!element) {
              return
            }
            element.style.opacity = '0'
            return element.pause()
          })
          const element = document.getElementById(`__clipara-navigation-carousel-${video.id}-${campaign.id}`)
          if (element?.src) {
            try {
              setVideoPlaying(true)
              element.play()
              element.style.opacity = '1'
            } catch (e) {
              console.log(e)
            }
          } else {
            await loadVideoLite({
              id: `__clipara-navigation-carousel-${video.id}`,
              video,
              campaign,
              useClip: true,
              playerName: 'navigation-carousel'
            })
            element.style.opacity = '1'
            setVideoPlaying(true)
          }
        }
      }}
      onMouseLeave={() => {
        if (widgetAutoplay && !isStories && video.type === 'VIDEO') {
          try {
            document.getElementById(`__clipara-navigation-carousel-${video.id}`).pause()
            document.getElementById(`__clipara-navigation-carousel-${video.id}`).currentTime = 0
            setVideoPlaying(false)
          } catch (e) {}
        }
      }}
    >
      {/* SPECIFIC DASH NO LINK */}
      <a href={undefined}>
        <ThumbnailHover flexDirection="column" key={video.id} width="auto">
          <Box className="thumbnail-button" display="none" position="absolute" top="24px" right="24px" zIndex="2">
            <ThumbnailContainer
              bg="white"
              borderRadius="20px"
              alignItems="center"
              justifyContent="center"
              width="40px"
              height="40px"
              boxShadow="0px 3px 8px -1px rgba(50, 50, 71, 0.05);filter: drop-shadow(0px 0px 1px rgba(12, 26, 75, 0.24))"
              cursor="pointer"
              minWidth="40px"
              onClick={() => {
                setAddThumbnailVideo(video)
                setShowAddThumbnailModal(true)
              }}
            >
              <Icon width="16px" height="16px" icon="image-black" />
            </ThumbnailContainer>
          </Box>

          <Box position="relative" height={videoHeight}>
            <VideoContainer isMobile={isMobile} isStories={isStories} videoPlaying={videoPlaying}>
              <Video
                widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                widgetThumbnailBorder={widgetThumbnailBorder}
                borderRadius={borderRadius}
                videoWidth={videoWidth}
                videoHeight={videoHeight}
                loop
                isCircle={isCircle}
                muted
                id={`__clipara-navigation-carousel-${video.id}`}
                preload="metadata"
                playsInline
                webkit-playsInline
                x-webkit-airplay="allow"
              />
            </VideoContainer>
            {isVideo ? (
              <StyledPicture>
                <source srcset={webpUrl} type="image/webp" />
                <source srcset={imageUrl} type="image/png" />
                <VideoImage
                  widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                  widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                  widgetThumbnailBorder={widgetThumbnailBorder}
                  borderRadius={borderRadius}
                  isCircle={isCircle}
                  loading="lazy"
                  src={imageUrl}
                  alt={video.name}
                />
              </StyledPicture>
            ) : (
              <VideoImage
                widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                widgetThumbnailBorder={widgetThumbnailBorder}
                borderRadius={borderRadius}
                isCircle={isCircle}
                loading="lazy"
                src={`https://clipara.b-cdn.net/${video.path}?aspect_ratio=${getAspectRatio(
                  campaign.widgetThumbnailDimensions
                )}&width=${Math.round(videoWidth * 2)}`}
                alt={video.name}
              />
            )}
            {showNextImage && nextImageUrl && nextWebpUrl ? (
              nextImageIsVideo ? (
                <StyledNextPicture>
                  <source srcset={nextWebpUrl} type="image/webp" />
                  <source srcset={nextImageUrl} type="image/png" />
                  <VideoImage
                    widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                    widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                    widgetThumbnailBorder={widgetThumbnailBorder}
                    borderRadius={borderRadius}
                    isCircle={isCircle}
                    loading="lazy"
                    src={nextImageUrl}
                    alt={video.name}
                  />
                </StyledNextPicture>
              ) : (
                <VideoImage
                  widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                  widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                  widgetThumbnailBorder={widgetThumbnailBorder}
                  borderRadius={borderRadius}
                  isCircle={isCircle}
                  loading="lazy"
                  src={nextImageImageUrl}
                  alt={video.name}
                />
              )
            ) : null}
            {isVideo ? <PlayButtonComponent pbWidth={60} campaign={campaign} /> : <ImageExpandButton />}

            {titleVideoObject &&
            widgetTitleActive &&
            (widgetTitlePosition === 'INSIDE' ||
              widgetTitlePosition === 'BOTTOM' ||
              widgetTitlePosition === 'MIDDLE' ||
              widgetTitlePosition === 'TOP') ? (
              <InnerTitleContainer borderRadius={campaign.borderRadius} widgetTitlePosition={widgetTitlePosition}>
                <Titles
                  {...campaign}
                  isMobile={isMobile}
                  isStories={isStories}
                  videoWidth={videoWidth}
                  titleVideoObject={titleVideoObject}
                />
              </InnerTitleContainer>
            ) : null}

            {isPopular && campaign.isDownloadCampaign ? <PopularButton campaign={campaign} isPopular={isPopular} /> : null}
          </Box>

          {titleVideoObject && widgetTitleActive && widgetTitlePosition === 'BELOW' ? (
            <Box mt="16px">
              <Titles
                {...campaign}
                isMobile={isMobile}
                isStories={isStories}
                videoWidth={videoWidth}
                gapWidth={widgetGapWidth}
                titleVideoObject={titleVideoObject}
                hideMarginLeft={i === 0}
                hideMarginRight={i === videos.length - 1}
              />
            </Box>
          ) : null}
        </ThumbnailHover>
      </a>
    </SlideItem>
  )
}

export default NavigationSlideItem
